import {
  Stack,
  Typography,
  useTheme,
  Avatar,
  Divider,
  Button,
} from "@mui/material";
import {
  RatingDistribution,
  RatingDistributionData,
} from "./ratings-distribution";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { ReviewModal } from "./review-modal";

export interface Review {
  id: number;
  userName: string;
  date: string;
  rating: number;
  comment: string;
}

export interface RatingsProps {
  reviews: Review[];
  averageRating?: number;
  totalReviews?: number;
  ratingDistribution?: RatingDistributionData[];
  hasMore?: boolean;
  onLoadMore?: () => void;
  productId?: any;
}

const defaultRatingData: RatingDistributionData[] = [
  { rating: 5, count: 25000, percentage: 60 },
  { rating: 4, count: 11000, percentage: 26 },
  { rating: 3, count: 8200, percentage: 20 },
  { rating: 2, count: 73900, percentage: 89 },
  { rating: 1, count: 2600, percentage: 6 },
];

const StarRating = ({
  rating,
  size = "18px",
}: {
  rating: number;
  size?: string;
}) => {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={0.5}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Typography
          key={star}
          sx={{
            color:
              star <= rating
                ? theme.palette.warning.main
                : theme.palette.grey[300],
            fontSize: size,
          }}
        >
          ★
        </Typography>
      ))}
    </Stack>
  );
};

const ReviewItem = ({ review }: { review: Review }) => {
  const theme = useTheme();

  const getInitials = (name: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  return (
    <Stack spacing={2} sx={{ py: { xs: 1, md: 4 } }}>
      <Stack direction="row" spacing={2} alignItems="center" gap={8}>
        <Stack alignItems={"center"}>
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              width: 40,
              height: 40,
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {getInitials(review.userName)}
          </Avatar>

          <Typography fontWeight="600" fontSize="16px" color="#000000">
            {review.userName}
          </Typography>

          <Typography
            sx={{
              color: "#596780",
              fontSize: "14px",
            }}
          >
            {review.date}
          </Typography>
        </Stack>
        <Stack spacing={1} sx={{ flex: 1 }}>
          <StarRating rating={review.rating} />
          <Typography
            sx={{
              color: "#212B36",
              fontSize: "15px",
              lineHeight: 1.6,
              mt: 1,
            }}
          >
            {review.comment}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

const Ratings = ({
  reviews,
  averageRating = 0,
  totalReviews = 0,
  hasMore,
  onLoadMore,
  ratingDistribution,
  productId,
}: RatingsProps) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Stack spacing={3}>
        <Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={{ xs: 4, sm: 0 }}
            alignItems="center"
            justifyContent={"space-between"}
            sx={{
              borderBottomWidth: `1px solid ${theme.palette.grey[200]}`,
              flex: 1,
            }}
          >
            <Stack>
              <Typography sx={{ color: "#596780", fontFamily: "Work Sans" }}>
                {"Average rating"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "32px",
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                }}
              >
                {averageRating}/5
              </Typography>
              <StarRating rating={Math.round(averageRating)} size="20px" />
              <Typography
                sx={{
                  color: theme.palette.grey[600],
                  fontSize: "14px",
                }}
              >
                ({totalReviews.toLocaleString()} reviews)
              </Typography>
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderStyle: "dashed",
                borderColor: theme.palette.grey[400],
                borderWidth: "1px",
                mx: 3,
              }}
            />

            <Stack sx={{ flex: 1 / 2 }}>
              <RatingDistribution
                data={ratingDistribution}
                totalReviews={totalReviews}
              />
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderStyle: "dashed",
                borderColor: theme.palette.grey[400],
                borderWidth: "1px",
              }}
            />

            <Stack>
              <Button
                variant="outlined"
                startIcon={<Icon icon="mdi:pencil-outline" />}
                onClick={handleOpenModal}
                sx={{
                  color: "black",
                  borderColor: "black",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "black",
                    backgroundColor: "rgba(0,0,0,0.05)",
                  },
                }}
              >
                Write your review
              </Button>
            </Stack>
          </Stack>
        </Stack>

        <Stack spacing={0}>
          {reviews.map((review) => (
            <ReviewItem key={review.id} review={review} />
          ))}
        </Stack>

        {hasMore && (
          <Stack alignItems="center" sx={{ pt: 2 }}>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: "15px",
                fontWeight: "600",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={onLoadMore}
            >
              Load More Reviews
            </Typography>
          </Stack>
        )}
      </Stack>

      <ReviewModal
        open={modalOpen}
        onClose={handleCloseModal}
        productId={productId}
      />
    </>
  );
};

export { Ratings, StarRating, ReviewItem };
